import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {forkJoin, of, Subscription} from 'rxjs';
import {ReviewService} from 'src/app/services/review.service';
import {LoaderService} from '../../../services/loader.service';
import {Review} from '../../../types/Review';
import {ReviewAnswer} from '../../../types/ReviewAnswer';
import {ToolsService} from '../../../services/tools.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {StaticPagesEnum} from '../../../enums/StaticPagesEnums';
import {ModalsControlService} from '../../../services/modals-control.service';
import {ModalCommand} from '../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {InfoModalContent} from '../../../types/InfoModalContent';
import {catchError, finalize} from 'rxjs/operators';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {ReviewQuestion} from '../../../types/ReviewQuestion';
import {RateComponentData} from '../../../types/RateComponentData';
import {MailchimpService} from '../../../services/mailchimp.service';

@Component({
  selector: 'app-rate',
  templateUrl: './rate.component.html',
  styleUrls: ['./rate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RateComponent implements OnInit, OnDestroy {
  userDataForm: UntypedFormGroup;
  hasRatingsError = false;
  formHasError = false;
  omdomenPolicyUrl: string[] = [];
  vilkorUrl: string[] = [];
  integritetsPolicyUrl: string[] = [];
  maxCommentLength: number = 600;
  maxNameLength: number = 64;
  maxEmailLength: number = 128;

  private review: Review = this.getEmptyReview();
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<RateComponent>,
    @Inject(MAT_DIALOG_DATA) public rateComponentData: RateComponentData,
    private reviewService: ReviewService,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private modalControl: ModalsControlService,
    private mailchimpService: MailchimpService,
    private _change: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.review = this.getEmptyReview();
    this.buildUserDataForm();
    this.omdomenPolicyUrl = this.urlBuilder.CreateStaticPageRouterLink(StaticPagesEnum.REVIEW);
    this.vilkorUrl = this.urlBuilder.CreateStaticPageRouterLink(StaticPagesEnum.TERMS);
    this.integritetsPolicyUrl = this.urlBuilder.CreateStaticPageRouterLink(StaticPagesEnum.PRIVACY);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  handleRateChanged(rateValue: number, questionId: number): void {
    const answer = this.getAnswerByQuestionId(questionId, this.review.reviewAnswers);
    answer.rating = rateValue;
  }

  // saveRating(): void {
  //   (Object as any).values(this.userDataForm.controls).forEach((control) => control.markAllAsTouched());
  //   this.hasRatingsError = this.hasRatingsErrorCheck();
  //   if (this.userDataForm.valid && this.hasRatingsError === false) {
  //     this.formHasError = false;
  //     this.updateReviewObjectFromFormData();
  //     const queryId: number = this.loaderService.generateQueueId();
  //     const queryItem: LoaderQueueItem = {
  //       id: queryId,
  //       title: '',
  //       description: '',
  //     };
  //     this.loaderService.addToQueue(queryItem);
  //     this.subscriptions.push(
  //       this.reviewService
  //         .AddReview(this.review)
  //         .pipe(
  //           finalize(() => {
  //             this.loaderService.removeFromQueue(queryId);
  //           }),
  //         )
  //         .subscribe(
  //           (result) => this.processReviewData(result),
  //           (error) => console.error(error),
  //         ),
  //     );
  //   } else {
  //     this.formHasError = true;
  //   }
  // }

  saveRating(): void {
    (Object as any).values(this.userDataForm.controls).forEach((control) => control.markAllAsTouched());
    this.hasRatingsError = this.hasRatingsErrorCheck();

    if (this.userDataForm.valid && this.hasRatingsError === false) {
      this.formHasError = false;
      this.updateReviewObjectFromFormData();

      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);

      const addReviewObservable = this.reviewService.AddReview(this.review);
      const subscribeNewsletterObservable = this.userDataForm.value.newsletter
        ? this.mailchimpService.SubscribeToList(this.userDataForm.value.email).pipe(catchError(() => of(null)))
        : of(null);

      this.subscriptions.push(
        forkJoin([addReviewObservable, subscribeNewsletterObservable])
          .pipe(finalize(() => this.loaderService.removeFromQueue(queryId)))
          .subscribe(
            ([reviewResult, newsletterResult]) => {
              this.processReviewData(reviewResult);
            },
            (error) => console.error('Error in one of the operations: ', error),
          ),
      );
    } else {
      this.formHasError = true;
    }
  }

  public reviewQuestionsTrackBy(i: number, el: ReviewQuestion) {
    return el ? el.id : null;
  }

  private hasRatingsErrorCheck(): boolean {
    return !this.tools.isNil(this.review.reviewAnswers.find((item) => item.rating === 0));
  }

  private processReviewData(result: Review): void {
    this.openInfoModal();
    this.review = this.getEmptyReview();
    this._change.markForCheck();
    this.dialogRef.close();
  }

  private openInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Tack för ditt omdöme!',
      message: `<p>Genom att du har lämnat ett omdöme på <b>${this.rateComponentData.name}</b> hjälper du både andra
      patienter men också mottagningen att utvecklas och förbättras.</p>`,
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }

  private buildUserDataForm(): void {
    this.userDataForm = this.formBuilder.group({
      firstname: [this.review.firstname, [Validators.required]],
      lastname: [this.review.lastname, [Validators.required]],
      email: [this.review.email, [Validators.required, Validators.email]],
      comment: [this.review.comment, []],
      policy: [false, [Validators.pattern('true')]],
      newsletter: [true, []],
    });
  }

  private getEmptyReview(): Review {
    const answers: ReviewAnswer[] = [];
    if (this.rateComponentData && this.rateComponentData.reviewSettings) {
      this.rateComponentData.reviewSettings.reviewQuestions.forEach((question) => {
        answers.push({
          rating: 0,
          reviewQuestionId: question.id,
        });
      });
    }

    return {
      caregiverUnitId: this.rateComponentData?.id,
      careServiceId: null,
      email: '',
      firstname: '',
      lastname: '',
      comment: '',
      isRecommended: false,
      reviewAnswers: answers,
      source: 0,
    };
  }

  private getAnswerByQuestionId(id: number, answers: ReviewAnswer[]): ReviewAnswer {
    return answers.find((answer) => answer.reviewQuestionId === id);
  }

  private updateReviewObjectFromFormData(): void {
    this.review.email = this.userDataForm.value.email;
    this.review.firstname = this.userDataForm.value.firstname;
    this.review.lastname = this.userDataForm.value.lastname;
    this.review.comment = this.userDataForm.value.comment;
  }
}
